// FeedViewItem.jsx

import React, { useMemo, useRef } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getRelativeTime } from 'utilities/DateTime';
import { PhotoShape } from 'utilities/Enums';
import { NodeTypeComponents } from 'utilities/NodeTypeComponents';
// Hooks
import useLogNodeView from 'node/hooks/useLogNodeView';

// CSS
import './FeedViewItem.css';

// Components
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import NodeCreatorCompact from 'node/creator/NodeCreatorCompact';
import ListViewItem from 'views/list/item/ListViewItem';
import { buildListViewItemData, resolveListViewItemConfig } from 'views/list/item/listViewItemData';

/**
 * FeedViewItem
 * 
 * Displays a single item in the node feed, including creator information,
 * action type, and the associated node card.
 * 
 * @param {object} node - The node data for the feed item.
 * @param {Boolean} compact - Whether to use a compact header layout.
 * @param {Function} onClick - Callback when the node is clicked.
 * @param {string|null} [shareBadge] - Optional share-state label for Gmail threads.
 * 
 * @returns {ReactNode} The rendered FeedViewItem component or null. 
 */
const FeedViewItem = ({ node, view = null, compact, onClick, shareBadge = null }) => {

  // ----- CONTEXT ----- //

  // Language Context
  const language = useCurrentLanguage();

  // Node Type Context
  const { getType } = useNodeTypes();

  // Theme Context
  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  // Mobile

  const isMobile = useIsMobile();

  // Node info
  const nodeKey = node?.key;
  const typeKey = node?.type;
  const typeDef = getType(typeKey) || null;

  // Creator name
  const creatorName = node?.user?.name;

  // Styles
  const backgroundColor = theme?.backgroundFaded || 'rgba(255, 255, 255, 0.04)';
  const borderColor = theme?.backgroundMuted || 'rgba(255, 255, 255, 0.1)';

  // Time info
  const relativeTime = getRelativeTime(node.dateModified);

  // Component props
  const componentProps = { node };

  // ----- REFS ----- //

  const containerRef = useRef(null);

  // Logs a view only if the feed item is actually visible for a moment.
  useLogNodeView(nodeKey, containerRef);

  // ----- MEMOS ----- //

  /**
   * Type-specific card only — no NODE/NodeCard fallback.
   * Types without their own cardComponent use ListViewItem below.
   */
  const CardComponent = useMemo(
    () => NodeTypeComponents[typeKey]?.cardComponent || null,
    [typeKey],
  );

  /**
   * Default list-item presentation for types without a dedicated card.
   */
  const listItemConfig = useMemo(() => {
    const hasExplicitProperties = Array.isArray(view?.properties) && view.properties.length > 0;
    return resolveListViewItemConfig({
      forceDefaultFields: !hasExplicitProperties,
      typeDef,
      view,
    });
  }, [typeDef, view]);

  const listItemData = useMemo(() => {
    if (CardComponent) {
      return {
        coverUrl: '',
        enumPills: [],
        fieldValues: [],
        subtitle: '',
      };
    }

    return buildListViewItemData({
      config: listItemConfig,
      language,
      node,
    });
  }, [CardComponent, language, listItemConfig, node]);

  // ----- HANDLERS ----- //

  /**
 * Handles open.
 *
 * @param {*} e
 */
  const handleOpen = (e) => {
    if (e?.stopPropagation) e.stopPropagation();

    if (
      typeKey === 'POST'
      && (!node?.data_post?.text || node.data_post.text.trim() === '')
      && Array.isArray(node?.data_post?.attachments)
      && node.data_post.attachments.length === 1
    ) {
      onClick(node.data_post.attachments[0]);
      return;
    }

    onClick(node);
  };

  if (!node) return null;

  const headerLabel = creatorName;

  // ----- RENDER ----- //

  return (

    <div
      className="feed-view-item-container"
      style={{
        backgroundColor: isMobile ? 'transparent' : backgroundColor,
        borderColor,
      }}
      ref={containerRef}
      role="button"
      onClick={onClick ? handleOpen : undefined}
    >
      {/* HEADER */}
      <div className="feed-view-item-header">

        {compact ? (
          <NodeCreatorCompact
            node={node}
          />
        ) : (
          <>
            <ProfilePhoto
              node={node?.user}
              size={16}
              borderWidth={0}
              shape={PhotoShape.ROUND}
            />

            {/* INFO */}
            <div className="feed-view-item-info">

              {/* CREATOR */}
              <div
                className="feed-view-item-creator"
              >
                {headerLabel}

                {/* ELAPSED */}
                {relativeTime && (

                  <span
                    className="feed-view-item-elapsed"
                  >
                    {relativeTime}
                  </span>
                )}
              </div>

            </div>

            {shareBadge ? (
              <span
                className="feed-view-item-share-badge"
                style={{ color: theme.foregroundMuted }}
              >
                {shareBadge}
              </span>
            ) : null}
          </>
        )}

      </div>

      {/* CARD COMPONENT */}
      <div className="feed-view-item-card">
        {CardComponent ? (
          <CardComponent {...componentProps} />
        ) : (
          <ListViewItem
            coverUrl={listItemData.coverUrl}
            enumPills={listItemData.enumPills}
            fieldValues={listItemData.fieldValues}
            node={node}
            subtitle={listItemData.subtitle}
          />
        )}
      </div>

      {/* FOOTER 
      <div className="feed-view-item-footer">

        <div className="feed-view-item-footer-left">

          <FeedViewItemButton
            iconKey={userHasFavorited ? 'bookmark_fill' : 'bookmark'}
            color={theme.foreground}
            title={`Favorite/Unfavorite This ${typeLabel}`}
            size={20}
            count={node?.favoriteCount || 0}
            onClick={handleFavoriteClick}
          />

          <FeedViewItemButton
            iconKey="share"
            color={theme.foreground}
            title={`Share This ${typeLabel}`}
            size={20}
            onClick={handleShareClick}
          />

        </div>

        <div className="feed-view-item-footer-right">

          <FeedViewItemButton
            iconKey="visibility"
            color={theme.foreground}
            title={`Views For This ${typeLabel}`}
            count={viewCount}
            size={20}
            alwaysShowCount
          />

        </div>

      </div>
      */}

    </div>

  );
};

// Dev Tools
FeedViewItem.displayName = 'FeedViewItem';

export default FeedViewItem;
